import { useState, type FormEvent } from 'react';
import { Button, Card, ErrorNotice, Field, Icon, StatusPill, Toggle, type Status } from '@hive/ui';
import { api, errorMessage, type EventType, type EventTypeInfo, type Webhook, type WebhookDelivery } from '../api/client';

const FORMATS: { format: Webhook['format']; label: string; placeholder: string; hint: string }[] = [
  { format: 'hive', label: 'Signed JSON', placeholder: 'https://crm.example.com/hive-events', hint: 'For your own systems; each delivery is signed.' },
  { format: 'slack', label: 'Slack', placeholder: 'https://hooks.slack.com/services/…', hint: 'A Slack incoming-webhook address.' },
  { format: 'teams', label: 'Microsoft Teams', placeholder: 'https://…webhook.office.com/…', hint: 'A Teams incoming-webhook address.' },
  { format: 'email', label: 'Email', placeholder: 'mailto:ops@example.com,oncall@example.com', hint: 'Needs SMTP set up on the server.' },
];
const FORMAT_LABEL = Object.fromEntries(FORMATS.map((f) => [f.format, f.label])) as Record<Webhook['format'], string>;
import { useLoad, useNow } from '../api/hooks';
import { ago } from '../format';
import { Dialog } from './Dialog';

const HEALTH: Record<Webhook['health'], { label: string; status: Status }> = {
  new: { label: 'No deliveries yet', status: 'idle' },
  healthy: { label: 'Delivering', status: 'healthy' },
  failing: { label: 'Failing', status: 'offline' },
};
const DELIVERY: Record<WebhookDelivery['status'], Status> = { delivered: 'healthy', pending: 'expiring', failed: 'offline' };

/** Webhook endpoints: where Hive tells the operator's systems about changes. */
export function WebhooksCard() {
  const now = useNow(30_000);
  const hooks = useLoad(async () => (await api.GET('/v1/integrations/webhooks')).data);
  const types = useLoad(async () => (await api.GET('/v1/integrations/event-types')).data);
  const [open, setOpen] = useState<{ kind: 'edit'; hook?: Webhook } | { kind: 'deliveries'; hook: Webhook } | { kind: 'secret'; secret: string; url: string } | null>(null);
  const [notice, setNotice] = useState<{ ok: boolean; text: string } | null>(null);

  async function test(hook: Webhook) {
    setNotice(null);
    const { data, error } = await api.POST('/v1/integrations/webhooks/{webhookId}/test', { params: { path: { webhookId: hook.id } } });
    void hooks.reload();
    if (!data) return setNotice({ ok: false, text: errorMessage(error) });
    setNotice(
      data.status === 'delivered'
        ? { ok: true, text: `Test delivered to ${hook.url} (HTTP ${data.lastStatusCode}).` }
        : { ok: false, text: `Test not delivered: ${data.lastError ?? 'no answer'}. It will be retried.` },
    );
  }

  async function rotate(hook: Webhook) {
    const { data, error } = await api.POST('/v1/integrations/webhooks/{webhookId}/secret', { params: { path: { webhookId: hook.id } } });
    if (!data) return setNotice({ ok: false, text: errorMessage(error) });
    setOpen({ kind: 'secret', secret: data.secret, url: hook.url });
  }

  async function remove(hook: Webhook) {
    const { error } = await api.DELETE('/v1/integrations/webhooks/{webhookId}', { params: { path: { webhookId: hook.id } } });
    if (error) return setNotice({ ok: false, text: errorMessage(error) });
    void hooks.reload();
  }

  return (
    <Card className="mc-table-card">
      <div className="mc-toolbar">
        <span className="mc-section-title">
          <Icon name="broadcast" size={16} />
          &nbsp;Webhooks and alert channels
        </span>
        <Button size="sm" variant="primary" onClick={() => setOpen({ kind: 'edit' })} disabled={!types.data}>
          <Icon name="plus" size={16} />
          Add webhook
        </Button>
      </div>
      {notice && <p className={`mc-note ${notice.ok ? 'mc-note--ok' : 'mc-note--bad'} mc-pad-note`}>{notice.text}</p>}
      <ul className="mc-rows mc-pad">
        {(hooks.data ?? []).map((h) => (
          <li key={h.id} className="mc-rows__row">
            <span className="mc-grow">
              <span className="mc-strong mc-wrap">{h.description ? `${h.description} · ` : ''}<code className="mc-small">{h.url}</code> <span className="mc-pill-soft">{FORMAT_LABEL[h.format]}</span></span>
              <span className="mc-small hv-muted">
                {h.events.length ? `${h.events.length} event${h.events.length === 1 ? '' : 's'}` : 'Every event'}
                {h.minSeverity === 'critical' ? ' · critical alerts only' : ''}
                {h.lastDeliveryAt ? ` · last delivered ${ago(h.lastDeliveryAt, now)} ago` : ''}
                {h.pending ? ` · ${h.pending} waiting to retry` : ''}
                {h.failingSince ? ` · failing for ${ago(h.failingSince, now)}` : ''}
              </span>
            </span>
            {h.active ? <StatusPill status={HEALTH[h.health].status}>{HEALTH[h.health].label}</StatusPill> : <StatusPill status="idle">Paused</StatusPill>}
            <Button variant="link" onClick={() => test(h)}>
              Send test
            </Button>
            <Button variant="link" onClick={() => setOpen({ kind: 'deliveries', hook: h })}>
              Deliveries
            </Button>
            <Button variant="link" onClick={() => setOpen({ kind: 'edit', hook: h })}>
              Edit
            </Button>
            {h.format === 'hive' && (
              <Button variant="link" onClick={() => rotate(h)}>
                New secret
              </Button>
            )}
            <Button variant="link" className="mc-link-danger" onClick={() => remove(h)}>
              Remove
            </Button>
          </li>
        ))}
        {hooks.data && hooks.data.length === 0 && (
          <li className="mc-table__empty">No webhooks yet. Add one so the CRM hears about changes without polling.</li>
        )}
      </ul>

      {open?.kind === 'edit' && types.data && (
        <WebhookDialog
          hook={open.hook}
          types={types.data}
          onClose={() => setOpen(null)}
          onSaved={(secret, url) => {
            void hooks.reload();
            setOpen(secret ? { kind: 'secret', secret, url } : null);
          }}
        />
      )}
      {open?.kind === 'deliveries' && <DeliveriesDialog hook={open.hook} onClose={() => setOpen(null)} />}
      {open?.kind === 'secret' && (
        <Dialog titleId="secret-title" title="Signing secret" subtitle={`For ${open.url}. Shown only once.`} onClose={() => setOpen(null)} width={560} footer={<Button variant="primary" onClick={() => setOpen(null)}>Done</Button>}>
          <div className="mc-form">
            <code className="mc-secret" aria-label="Signing secret">
              {open.secret}
            </code>
            <p className="mc-small hv-muted">
              The receiver checks each delivery&apos;s Hive-Signature header: HMAC-SHA256 of &quot;timestamp.body&quot; with this secret. The
              contract&apos;s webhooks section has the details.
            </p>
          </div>
        </Dialog>
      )}
    </Card>
  );
}

function WebhookDialog({ hook, types, onClose, onSaved }: { hook?: Webhook; types: EventTypeInfo[]; onClose(): void; onSaved(secret: string | null, url: string): void }) {
  const [format, setFormat] = useState<Webhook['format']>(hook?.format ?? 'hive');
  const [minSeverity, setMinSeverity] = useState<'warning' | 'critical'>(hook?.minSeverity ?? 'warning');
  const [url, setUrl] = useState(hook?.url ?? 'https://');
  const fmt = FORMATS.find((f) => f.format === format)!;
  const [description, setDescription] = useState(hook?.description ?? '');
  const [all, setAll] = useState(!hook || hook.events.length === 0);
  const [events, setEvents] = useState<EventType[]>(hook?.events ?? []);
  const [active, setActive] = useState(hook?.active ?? true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const choosable = types.filter((t) => t.type !== 'webhook.test');

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (!all && events.length === 0) return setError('Choose at least one event, or send every event.');
    setBusy(true);
    setError(null);
    const body = { url: url.trim(), description: description.trim(), events: all ? [] : events, format, minSeverity };
    if (hook) {
      const { data, error: err } = await api.PATCH('/v1/integrations/webhooks/{webhookId}', { params: { path: { webhookId: hook.id } }, body: { ...body, active } });
      setBusy(false);
      if (!data) return setError(errorMessage(err));
      onSaved(null, data.url);
    } else {
      const { data, error: err } = await api.POST('/v1/integrations/webhooks', { body });
      setBusy(false);
      if (!data) return setError(errorMessage(err));
      onSaved(format === 'hive' ? data.secret : null, data.webhook.url);
    }
  }

  return (
    <Dialog
      titleId="hook-title"
      title={hook ? 'Edit channel' : 'Add a webhook or alert channel'}
      subtitle="Where Hive sends events and alerts: your systems, Slack, Teams or email."
      onClose={onClose}
      width={620}
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="primary" type="submit" form="hook-form" disabled={busy}>
            {hook ? 'Save' : 'Add webhook'}
          </Button>
        </>
      }
    >
      <form id="hook-form" className="mc-form" onSubmit={submit}>
        {error && <ErrorNotice title={error} />}
        <div className="hv-field">
          <label className="hv-field__label" htmlFor="h-format">
            Send as
          </label>
          <select
            id="h-format"
            className="hv-input"
            value={format}
            onChange={(e) => {
              const next = e.target.value as Webhook['format'];
              setFormat(next);
              if (next === 'email' && !url.startsWith('mailto:')) setUrl('mailto:');
              if (next !== 'email' && url.startsWith('mailto:')) setUrl('https://');
            }}
          >
            {FORMATS.map((f) => (
              <option key={f.format} value={f.format}>
                {f.label}
              </option>
            ))}
          </select>
          <span className="mc-tiny hv-muted">{fmt.hint}</span>
        </div>
        <Field id="h-url" label={format === 'email' ? 'Email addresses' : 'Address'} placeholder={fmt.placeholder} value={url} onChange={(e) => setUrl(e.target.value)} required maxLength={2000} />
        <div className="hv-field">
          <label className="hv-field__label" htmlFor="h-sev">
            Alerts
          </label>
          <select id="h-sev" className="hv-input" value={minSeverity} onChange={(e) => setMinSeverity(e.target.value as 'warning' | 'critical')}>
            <option value="warning">Critical alerts and warnings</option>
            <option value="critical">Critical alerts only</option>
          </select>
        </div>
        <Field id="h-desc" label="Description (optional)" placeholder="Salesforce" value={description} onChange={(e) => setDescription(e.target.value)} maxLength={120} />
        {hook && <Toggle label={active ? 'Active' : 'Paused: nothing is queued while paused'} checked={active} onChange={setActive} />}
        <fieldset className="mc-fieldset">
          <legend className="hv-field__label">Events</legend>
          <Toggle label="Send every event, including new ones added later" checked={all} onChange={setAll} />
          {!all && (
            <div className="mc-checkgrid">
              {choosable.map((t) => (
                <label key={t.type} className="mc-check">
                  <input
                    type="checkbox"
                    checked={events.includes(t.type)}
                    onChange={(e) => setEvents((list) => (e.target.checked ? [...list, t.type] : list.filter((x) => x !== t.type)))}
                  />
                  <span>
                    <code className="mc-small">{t.type}</code>
                    <span className="mc-tiny hv-muted mc-line">{t.description}</span>
                  </span>
                </label>
              ))}
            </div>
          )}
        </fieldset>
      </form>
    </Dialog>
  );
}

function DeliveriesDialog({ hook, onClose }: { hook: Webhook; onClose(): void }) {
  const now = useNow(10_000);
  const list = useLoad(async () => (await api.GET('/v1/integrations/webhooks/{webhookId}/deliveries', { params: { path: { webhookId: hook.id } } })).data, [hook.id]);
  return (
    <Dialog titleId="del-title" title="Deliveries" subtitle={hook.url} onClose={onClose} width={760} footer={<Button onClick={() => void list.reload()}>Refresh</Button>}>
      <table className="mc-table mc-table--compact">
        <thead>
          <tr>
            <th scope="col">Queued</th>
            <th scope="col">Event</th>
            <th scope="col">Status</th>
            <th scope="col">Attempts</th>
            <th scope="col">Last result</th>
          </tr>
        </thead>
        <tbody>
          {(list.data ?? []).map((d) => (
            <tr key={d.id}>
              <td className="mc-small">{ago(d.createdAt, now)} ago</td>
              <td>
                <code className="mc-small">{d.eventType}</code>
              </td>
              <td>
                <StatusPill status={DELIVERY[d.status]}>
                  {d.status === 'delivered' ? 'Delivered' : d.status === 'failed' ? 'Gave up' : 'Retrying'}
                </StatusPill>
              </td>
              <td className="mc-small">{d.attempts}</td>
              <td className="mc-small">
                {d.lastError ?? (d.lastStatusCode ? `HTTP ${d.lastStatusCode}` : '—')}
                {d.status === 'pending' && d.nextAttemptAt ? ` · next try ${new Date(d.nextAttemptAt).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}` : ''}
              </td>
            </tr>
          ))}
          {list.data && list.data.length === 0 && (
            <tr>
              <td colSpan={5} className="mc-table__empty">
                Nothing sent yet.
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </Dialog>
  );
}
